import Icon from "../../components/Icon"
import { formatMoney } from "../../utils/format"
import { getUtilityInvoiceSettlement } from "./utilitySettlement"
import { getCustomerInitials } from "./utbillsHelpers"
import SearchDropdown from "./SearchDropdown"

// One customer can appear on many bills. Group them by the customer record so
// the picker offers each person once, with their own bill count and debt.
export const getInvoiceCustomerKey = (invoice) => {
  const customer = invoice?.customer
  const customerId = customer && typeof customer === "object" ? customer._id : customer
  return String(
    customerId || invoice?.customerPhone || invoice?.customerIdNumber || invoice?.customerName || ""
  )
}

const getInvoiceCustomerName = (invoice) =>
  invoice?.customerName || invoice?.customer?.name || invoice?.customerIdNumber || "Customer"

export function buildBillCustomers(invoices = []) {
  const byKey = new Map()

  invoices.forEach((invoice) => {
    const key = getInvoiceCustomerKey(invoice)
    if (!key) return
    if (!byKey.has(key)) {
      byKey.set(key, {
        key,
        name: getInvoiceCustomerName(invoice),
        phone: invoice?.customerPhone || invoice?.customer?.phone || "",
        idNumber: invoice?.customerIdNumber || invoice?.customer?.idNumber || "",
        billCount: 0,
        totalDue: 0,
      })
    }
    const row = byKey.get(key)
    row.billCount += 1
    row.totalDue += getUtilityInvoiceSettlement(invoice).outstandingAmount
    if (!row.phone) row.phone = invoice?.customerPhone || invoice?.customer?.phone || ""
    if (!row.idNumber) row.idNumber = invoice?.customerIdNumber || invoice?.customer?.idNumber || ""
  })

  return [...byKey.values()]
    .map((row) => ({ ...row, totalDue: Math.round(row.totalDue * 100) / 100 }))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
}

// Picking the customer first, instead of filtering the table on every letter,
// keeps a long bill list still while the user is still deciding who to look at.
function CustomerBillFilter({ invoices = [], selectedKey = "", onSelect }) {
  const customers = buildBillCustomers(invoices)
  const selected = customers.find((customer) => customer.key === String(selectedKey)) || null

  const options = customers.map((customer) => ({
    key: customer.key,
    label: customer.name,
    searchText: [customer.name, customer.phone, customer.idNumber].filter(Boolean).join(" "),
    customer,
  }))

  // Once someone is picked, their bar replaces the search box instead of
  // sitting under it: the name was showing twice and eating a third of the
  // phone screen before the first bill row.
  if (selected) {
    return (
      <div className="flex items-center gap-2.5 rounded-xl border border-teal-200 bg-teal-50 px-3 py-2">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-600 text-[11px] font-black text-white">
          {getCustomerInitials(selected.name)}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-black leading-tight text-slate-900">{selected.name}</p>
          <p className="truncate text-[11px] text-slate-500">
            {selected.phone || selected.idNumber || "No contact saved"}
            {` · ${selected.billCount === 1 ? "1 bill" : `${selected.billCount} bills`}`}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[9px] font-bold uppercase tracking-wide text-slate-500">Total due</p>
          <p
            className={`text-sm font-black leading-tight tabular-nums ${
              selected.totalDue > 0 ? "text-rose-600" : "text-emerald-700"
            }`}
          >
            {formatMoney(selected.totalDue)}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onSelect("")}
          aria-label={`Stop showing only ${selected.name}`}
          title="Back to all customers"
          className="shrink-0 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-white hover:text-rose-600"
        >
          <Icon name="x-lg" size="0.8rem" />
        </button>
      </div>
    )
  }

  return (
    <SearchDropdown
      options={options}
      selectedKey={selectedKey}
      onSelect={(option) => onSelect(option.key)}
      onClear={() => onSelect("")}
      placeholder="Search customer by name, ID or phone"
      emptyText="No customer matches your search."
      renderOption={({ customer }) => (
        <span className="flex min-w-0 flex-1 items-center gap-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[10px] font-black text-slate-600">
            {getCustomerInitials(customer.name)}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate font-bold text-slate-800">{customer.name}</span>
            <span className="block truncate text-[11px] text-slate-500">
              {customer.phone || customer.idNumber || "No contact saved"}
              {` · ${customer.billCount === 1 ? "1 bill" : `${customer.billCount} bills`}`}
            </span>
          </span>
          <span
            className={`shrink-0 text-xs font-black tabular-nums ${
              customer.totalDue > 0 ? "text-rose-600" : "text-emerald-700"
            }`}
          >
            {formatMoney(customer.totalDue)}
          </span>
        </span>
      )}
    />
  )
}

export default CustomerBillFilter
